import {Component, Input, AfterViewInit, OnDestroy, Output, EventEmitter} from '@angular/core';
import {CaptchaSize, CaptchaTheme, CaptchaType, captchaSizes, captchaThemes, captchaTypes} from '../../constants/captcha.constants';
import * as uuid from 'uuid';
import {CaptchaResponse} from '../../models/captcha.model';

declare let turnstile: any;
declare let grecaptcha: any;

@Component({
  selector: 'si-captcha',
  templateUrl: './captcha.component.html',
  styleUrls: ['./captcha.component.scss']
})
export class CaptchaComponent implements AfterViewInit, OnDestroy {
  @Input() type: CaptchaType;
  @Input() scriptUrl: string;
  @Input() siteKey: string;
  @Input() size: CaptchaSize = captchaSizes.NORMAL;
  @Input() theme: CaptchaTheme = captchaThemes.LIGHT;
  @Input() captchaResponse: CaptchaResponse;
  @Input() captchaResponseError: CaptchaResponse;
  @Output() captchaResponseChange = new EventEmitter<CaptchaResponse>();
  @Output() captchaResponseErrorChange = new EventEmitter<CaptchaResponse>();
  @Output() captchaRenderedChange = new EventEmitter<boolean>();
  errorMessage: string;
  private _reset: number;

  get reset(): number {
    return this._reset;
  }

  @Input()
  set reset(val) {
    this._reset = val;
    if (val) {
      this.resetCaptcha();
    }
  }

  ngAfterViewInit(): void {
    this.onCaptchaResponseReceived(null); // New captcha means new response, so old response are not valid.
    if (!this.type || !this.scriptUrl || !this.siteKey) {
      return;
    }

    this.loadCaptchaScript()
      .then(() => this.renderCaptcha())
      .catch(error => this.handleCaptchaError(error));
  }

  loadCaptchaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      switch (this.type) {
        case captchaTypes.TURNSTILE:
        case captchaTypes.RECAPTCHA:
          try {
            if (document.querySelector(`script[src*="${this.scriptUrl}"]`)) {
              resolve();
            } else {
              const script = document.createElement('script');
              script.src = this.scriptUrl;
              script.async = true;
              script.defer = true;
              script.onload = () => resolve();
              script.onerror = () => reject(new Error('Failed to load CAPTCHA script'));
              document.head.appendChild(script);
            }
          } catch (error) {
            this.handleCaptchaError(error);
          }

          break;
        default:
          reject(new Error('Unsupported CAPTCHA type'));
      }
    });
  }

  renderCaptcha(): void {
    const container = document.querySelector('#captcha-container') as HTMLElement;
    switch (this.type) {
      case captchaTypes.TURNSTILE:
        this.renderTurnstileCaptcha(container);
        break;
      case captchaTypes.RECAPTCHA:
        // For some reason 'script.onload = () => resolve();' is executing before ReCaptcha script is fully loaded
        // So, this timeout is here to wait fully load of ReCaptcha script before 'grecaptcha.render'
        setTimeout(() => {
          this.renderRecaptchaCaptcha(container);
        }, 1000);
        break;
      default:
        this.handleCaptchaError(new Error('Unsupported CAPTCHA type'));
    }
  }

  renderTurnstileCaptcha(container: HTMLElement): void {
    try {
      if (!turnstile) {
        this.handleCaptchaError(new Error('Turnstile script not loaded'));
      }

      turnstile.render(container, {
        sitekey: this.siteKey,
        size: this.size,
        theme: this.theme,
        callback: (token: string) => {
          this.onCaptchaResponseReceived(token);
        },
        'error-callback': (token: string) => {
          this.onCaptchaResponseError(token);
        },
      });
      this.handleCaptchaSuccess();
    } catch (error) {
      this.handleCaptchaError(error);
    }
  }

  renderRecaptchaCaptcha(container: HTMLElement): void {
    try {
      if (!grecaptcha) {
        this.handleCaptchaError(new Error('reCAPTCHA script not loaded'));
      }

      grecaptcha.render(container, {
        sitekey: this.siteKey,
        size: this.size,
        theme: this.theme,
        callback: (token: string) => {
          this.onCaptchaResponseReceived(token);
        },
        'error-callback': (token: string) => {
          this.onCaptchaResponseError(token);
        },
      });
      this.handleCaptchaSuccess();
    } catch (error) {
      this.handleCaptchaError(error);
    }
  }

  onCaptchaResponseReceived(token: string): void {
    this.captchaResponse = {captchaKey: token, idempotentKey: uuid.v4()} as CaptchaResponse;
    this.captchaResponseChange.emit(this.captchaResponse);
  }

  onCaptchaResponseError(token: string): void {
    this.captchaResponseError = {captchaKey: token, idempotentKey: uuid.v4()} as CaptchaResponse;
    this.captchaResponseErrorChange.emit(this.captchaResponseError);
  }

  handleCaptchaError(error: Error): void {
    this.errorMessage = error.message;
    this.captchaRenderedChange.emit(false);
    console.error('CAPTCHA Error:', error.message);
  }

  handleCaptchaSuccess(): void {
    this.captchaRenderedChange.emit(true);
  }

  resetCaptcha(): void {
    this.onCaptchaResponseReceived(null);
    switch (this.type) {
      case captchaTypes.TURNSTILE:
        turnstile?.reset();
        break;
      case captchaTypes.RECAPTCHA:
        grecaptcha?.reset();
        break;
      default:
        this.handleCaptchaError(new Error('Unsupported CAPTCHA type'));
    }
  }

  ngOnDestroy(): void {
    const container = document.querySelector('#captcha-container') as HTMLElement;
    if (container) {
      container.innerHTML = '';
    }
  }
}
